import { useEffect, useMemo, useState } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import {
  MapPin,
  Phone,
  MessageCircle,
  Clock,
  Navigation,
  Instagram,
  Facebook,
  Youtube,
  Mail,
  Linkedin,
  Twitter,
  Building2,
} from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import {
  formatOpeningHoursDayRange,
  getOpeningHoursDay,
  hasOpeningHoursData,
} from '@/lib/openingHours';
import { Layout5Root } from './Layout5Root';
import './styles.css';
import './contact.css';
import { openStoreDirections } from '@/lib/storeMapUrls';

const WEEK_DAYS = [
  { key: 'monday', label: 'Segunda-feira', short: 'Seg' },
  { key: 'tuesday', label: 'Terça-feira', short: 'Ter' },
  { key: 'wednesday', label: 'Quarta-feira', short: 'Qua' },
  { key: 'thursday', label: 'Quinta-feira', short: 'Qui' },
  { key: 'friday', label: 'Sexta-feira', short: 'Sex' },
  { key: 'saturday', label: 'Sábado', short: 'Sáb' },
  { key: 'sunday', label: 'Domingo', short: 'Dom' },
] as const;

const DAY_KEYS_BY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

type DayKey = (typeof WEEK_DAYS)[number]['key'];

type DaySchedule = {
  key: DayKey;
  label: string;
  hours: string;
  isClosed: boolean;
  isToday: boolean;
};

type StoreStatus = {
  isOpen: boolean;
  label: string;
  detail: string;
};

type Store = {
  _id?: string;
  company?: string;
  frontage?: string;
  phone?: { ios?: string; formatted?: string };
  whatsapp?: { ios?: string; formatted?: string };
  place?: { address?: string; loc?: number[] };
  openingHours?: { default?: Record<string, { from?: string; to?: string }> };
};

const parseTimeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

const getCurrentDayKey = (): DayKey => DAY_KEYS_BY_INDEX[new Date().getDay()] as DayKey;

const getDayHours = (openingHours: Store['openingHours'], dayKey: string) =>
  getOpeningHoursDay(openingHours, dayKey);

const getStoreStatus = (openingHours: Store['openingHours']): StoreStatus => {
  const todayKey = getCurrentDayKey();
  const todayHours = getDayHours(openingHours, todayKey);
  const currentMinutes = new Date().getHours() * 60 + new Date().getMinutes();

  if (todayHours?.from && todayHours?.to) {
    const from = parseTimeToMinutes(todayHours.from);
    const to = parseTimeToMinutes(todayHours.to);

    if (currentMinutes >= from && currentMinutes < to) {
      return { isOpen: true, label: 'Aberto agora', detail: `Até às ${todayHours.to}` };
    }
    if (currentMinutes < from) {
      return { isOpen: false, label: 'Fechado agora', detail: `Abrimos hoje às ${todayHours.from}` };
    }
  }

  const todayIndex = WEEK_DAYS.findIndex((d) => d.key === todayKey);
  for (let offset = 1; offset <= 7; offset += 1) {
    const nextDay = WEEK_DAYS[(todayIndex + offset) % 7];
    const nextHours = getDayHours(openingHours, nextDay.key);
    if (nextHours?.from && nextHours?.to) {
      const prefix = offset === 1 ? 'Amanhã' : nextDay.label;
      return { isOpen: false, label: 'Fechado agora', detail: `${prefix} às ${nextHours.from}` };
    }
  }

  return { isOpen: false, label: 'Fechado agora', detail: 'Veja os horários abaixo' };
};

const formatOpeningHours = (openingHours: Store['openingHours']): DaySchedule[] => {
  const todayKey = getCurrentDayKey();
  return WEEK_DAYS.map((day) => {
    const range = formatOpeningHoursDayRange(openingHours, day.key);
    const isClosed = range === 'Fechado';
    return {
      key: day.key,
      label: day.label,
      hours: isClosed ? 'Fechado' : range.replace(' - ', ' – '),
      isClosed,
      isToday: day.key === todayKey,
    };
  });
};

const openMapsDirections = (targetStore: Store) => {
  const place = targetStore.place;
  openStoreDirections(place);
};

const OpeningHoursList = ({ schedules, status }: { schedules: DaySchedule[]; status: StoreStatus }) => (
  <div className="wl5-contact-hours">
    <h3 className="wl5-contact-hours-title">
      <Clock className="h-4 w-4" style={{ color: 'var(--wl5-button)' }} />
      Horário de funcionamento
    </h3>
    <p className="wl5-contact-hours-detail">{status.detail}</p>
    <div className="wl5-contact-hours-list" role="list">
      {schedules.map((schedule) => (
        <div
          key={schedule.key}
          role="listitem"
          className={`wl5-contact-hours-row ${schedule.isToday ? 'wl5-contact-hours-row--today' : ''}`}
          aria-current={schedule.isToday ? 'date' : undefined}
        >
          <span className="wl5-contact-hours-day">{schedule.label}</span>
          <span className={`wl5-contact-hours-time ${schedule.isClosed ? 'is-closed' : ''}`}>
            {schedule.hours}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const Contact = () => {
  const { config } = useAppData();
  const allStores = config.stores || [];
  const { colors } = useTheme();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const buttonColor = colors.button;
  const titleColor = colors.title;
  const textColor = colors.text;

  const socialLinks = useMemo(() => {
    const networks = config.socialNetworks || {};
    return [
      networks.instagram && { href: networks.instagram, label: 'Instagram', icon: Instagram },
      networks.facebook && { href: networks.facebook, label: 'Facebook', icon: Facebook },
      networks.youtube && { href: networks.youtube, label: 'YouTube', icon: Youtube },
      networks.linkedin && { href: networks.linkedin, label: 'LinkedIn', icon: Linkedin },
      networks.twitter && { href: networks.twitter, label: 'X', icon: Twitter },
      networks.tiktok && { href: networks.tiktok, label: 'TikTok', icon: null },
    ].filter(Boolean) as Array<{ href: string; label: string; icon: typeof Instagram | null }>;
  }, [config.socialNetworks]);

  const hasSocialBlock = socialLinks.length > 0 || !!config.email?.trim();

  useEffect(() => {
    document.title = `Contato - ${config.name}`;
  }, [config.name]);

  const handleOpenMaps = async (targetStore: Store) => {
    if (!userLocation) {
      const location = await requestUserLocation();
      if (location) setUserLocation(location);
    }
    openMapsDirections(targetStore);
  };

  return (
    <Layout5Root withHeader className="wl5-contact-page">
      <Header config={config} />

      <section className="wl5-contact-top">
        <div className="wl5-container">
          <div className="wl5-contact-top-head">
            <p className="wl5-eyebrow">Contato</p>
            <h1 className="wl5-contact-top-title" style={{ color: titleColor }}>
              Fale conosco
            </h1>
            <p className="wl5-contact-top-lead" style={{ color: textColor }}>
              Tire dúvidas, agende uma visita ou fale com nossa equipe.
            </p>
            {config.about && (
              <p className="wl5-contact-top-about">{config.about}</p>
            )}
          </div>
        </div>
      </section>

      <section className="wl5-contact-stores">
        <div className="wl5-container">
          <div className="wl5-contact-stores-head">
            <p className="wl5-eyebrow">Nossas lojas</p>
            <h2 className="wl5-contact-stores-title" style={{ color: titleColor }}>
              Visite-nos
            </h2>
          </div>

          <div className="wl5-contact-stores-list">
            {allStores.map((item, index) => {
              const schedules = hasOpeningHoursData(item.openingHours)
                ? formatOpeningHours(item.openingHours)
                : [];
              const status = hasOpeningHoursData(item.openingHours)
                ? getStoreStatus(item.openingHours)
                : null;
              const distance = getDistanceToStore(userLocation, item);

              return (
                <article key={item._id || index} className="wl5-contact-store">
                  <div className="wl5-contact-store-main">
                    <div className="wl5-contact-store-top">
                      <div>
                        <h3 className="wl5-contact-store-name" style={{ color: titleColor }}>
                          {item.company}
                        </h3>
                        {item.place?.address && (
                          <p className="wl5-contact-store-address" style={{ color: textColor }}>
                            {item.place.address}
                          </p>
                        )}
                      </div>
                      <div className="wl5-contact-store-badges">
                        {status && (
                          <span className={`wl5-contact-badge ${status.isOpen ? 'wl5-contact-badge--open' : 'wl5-contact-badge--closed'}`}>
                            <span className="wl5-contact-badge-dot" aria-hidden="true" />
                            {status.label}
                          </span>
                        )}
                        {distance !== null && (
                          <span
                            className="wl5-contact-badge wl5-contact-badge--distance"
                            style={{ backgroundColor: colors.secondary }}
                          >
                            <Navigation className="h-3 w-3" />
                            {formatDistance(distance)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="wl5-contact-store-actions">
                      {item.phone?.ios && (
                        <a href={item.phone.ios} className="wl5-contact-store-action">
                          <span
                            className="wl5-contact-store-action-icon"
                            style={{ backgroundColor: `${buttonColor}12`, color: buttonColor }}
                          >
                            <Phone className="h-4 w-4" />
                          </span>
                          Ligar
                        </a>
                      )}

                      {item.whatsapp?.ios && (
                        <button
                          type="button"
                          onClick={() => openWhatsApp(item.whatsapp?.ios)}
                          className="wl5-contact-store-action"
                        >
                          <span
                            className="wl5-contact-store-action-icon"
                            style={{ backgroundColor: '#22c55e18', color: '#16a34a' }}
                          >
                            <MessageCircle className="h-4 w-4" />
                          </span>
                          WhatsApp
                        </button>
                      )}

                      {item.place && (
                        <button
                          type="button"
                          onClick={() => handleOpenMaps(item)}
                          className="wl5-contact-store-action"
                        >
                          <span
                            className="wl5-contact-store-action-icon"
                            style={{ backgroundColor: '#ef444418', color: '#dc2626' }}
                          >
                            <MapPin className="h-4 w-4" />
                          </span>
                          Rotas
                        </button>
                      )}
                    </div>

                    {schedules.length > 0 && status && (
                      <OpeningHoursList schedules={schedules} status={status} />
                    )}
                  </div>

                  <div className="wl5-contact-store-side">
                    {item.frontage ? (
                      <img src={item.frontage} alt={`Fachada ${item.company}`} />
                    ) : (
                      <div className="wl5-contact-store-side-placeholder">
                        <Building2 className="h-12 w-12 opacity-30" />
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {hasSocialBlock && (
        <section className="wl5-contact-social-v2">
          <div className="wl5-container">
            <div className="wl5-contact-social-v2-inner">
              <div>
                <p className="wl5-eyebrow">Redes sociais</p>
                <h2 className="wl5-contact-social-v2-title" style={{ color: titleColor }}>
                  Conecte-se conosco
                </h2>
              </div>
              <div className="wl5-contact-social-v2-links">
                {socialLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wl5-contact-social-circle"
                    >
                      <span className="wl5-contact-social-circle-icon">
                        {Icon ? (
                          <Icon className="h-5 w-5" style={{ color: buttonColor }} />
                        ) : (
                          <span className="text-xs font-bold" style={{ color: buttonColor }}>TT</span>
                        )}
                      </span>
                      <span className="wl5-contact-social-circle-label">{link.label}</span>
                    </a>
                  );
                })}
                {config.email?.trim() && (
                  <a href={`mailto:${config.email}`} className="wl5-contact-social-circle">
                    <span className="wl5-contact-social-circle-icon">
                      <Mail className="h-5 w-5" style={{ color: buttonColor }} />
                    </span>
                    <span className="wl5-contact-social-circle-label">E-mail</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer config={config} />
    </Layout5Root>
  );
};

export default Contact;
